import React from "react";
import { motion } from "framer-motion";
import { Sparkles, Phone, Mail, ExternalLink, Heart, Compass, Leaf, BookOpen } from "lucide-react";
import { ASSETS, CONTACT_INFO } from "@/data/site";

const benefits = [
  "Gaining clarity on your life direction and purpose",
  "Reducing anxiety, stress, and overwhelm",
  "Releasing tension held in the body",
  "Deepening emotional clarity and self-awareness",
  "Nurturing spiritual and personal growth",
];

const externalLinks = [
  {
    icon: ExternalLink,
    label: "Visit vernacarrspiritualhealing.com",
    href: "https://vernacarrspiritualhealing.com",
    sub: "Verna's dedicated healing website",
  },
  {
    icon: Leaf,
    label: "Visit lightheartedhealingcentre.com",
    href: "https://lightheartedhealingcentre.com",
    sub: "Workshops & retreats — Mercury Bay, Coromandel",
  },
  {
    icon: BookOpen,
    label: "Featured article in Coromind",
    href: "https://coromind.nz/the-power-of-conscious-energy-healing/",
    sub: "The Power of Conscious Energy Healing",
  },
  {
    icon: Leaf,
    label: "Free eBook download",
    href: "https://vernacarrspiritualhealing.com/wp-content/uploads/2023/11/Wisdom-for-changing-consciousness-2023.pdf",
    sub: "Wisdom for Changing Consciousness",
  },
];

const EnergyHealingSection = () => {
  return (
    <section id="healing" className="relative section section-light" data-testid="healing-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <p className="text-xs tracking-[0.3em] uppercase mb-3" style={{ color: "var(--cs-purple)" }}>Beyond the cards</p>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="font-serif-display text-5xl md:text-6xl leading-tight"
            style={{ color: "var(--cs-navy)" }}
            data-testid="healing-heading"
          >
            Intuitive <span className="italic" style={{ color: "var(--cs-purple)" }}>Energy Healing</span>
          </motion.h2>
          <p className="mt-5 text-lg leading-relaxed" style={{ color: "#475569" }}>
            Verna is also an Intuitive Energy Healing Facilitator with over 35 years of experience working with the body&apos;s subtle energy systems.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-10 lg:gap-14 items-start">
          {/* LEFT image */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-2 relative lg:sticky lg:top-28"
          >
            <div className="absolute -inset-5 rounded-3xl blur-3xl opacity-30"
                 style={{ background: "radial-gradient(circle, rgba(153,76,153,0.45), transparent 70%)" }} />
            <div className="relative rounded-3xl overflow-hidden border border-[#131a48]/10"
                 style={{ boxShadow: "0 20px 60px rgba(19,26,72,0.20)" }}>
              <img
                src={ASSETS.vernaHealing}
                alt="Verna facilitating an energy healing session"
                className="w-full h-auto object-cover"
                data-testid="healing-image"
              />
            </div>
          </motion.div>

          {/* RIGHT content */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="lg:col-span-3 space-y-6"
          >
            <div className="box-pastel-lavender p-8 md:p-10" data-testid="healing-intro" style={{ boxShadow: "0 12px 40px rgba(74,42,114,0.22)" }}>
              <p className="text-[#2a1f4a] text-lg leading-relaxed">
                Verna brings deep wisdom, compassion, and intuitive insight to every healing session. Energy healing works through intention and alignment with your higher self. You may not fully understand what occurs on a physical level — but on deep spiritual, energetic, and quantum levels, profound shifts take place.
              </p>
              <p className="mt-5 text-[#2a1f4a] text-lg leading-relaxed">
                Verna&apos;s work is rooted in a fundamental truth:{" "}
                <span className="italic font-medium" style={{ color: "var(--cs-purple)" }}>
                  no one can heal for another.
                </span>{" "}
                This is why she describes herself as a <em>facilitator</em> of healing — her gift is connecting you with your own higher guidance and inner wisdom, so that true healing can unfold.
              </p>
              <p className="mt-5 text-[#2a1f4a] text-lg leading-relaxed">
                Working with you as a guide, Verna helps identify where your energy may be stagnant or disrupted — patterns often rooted in stress, trauma, or suppressed emotion — and gently supports you in releasing what no longer serves you.
              </p>
            </div>

            {/* How a Session Can Help */}
            <div className="box-pastel-lavender p-8 md:p-10" data-testid="healing-benefits" style={{ boxShadow: "0 12px 40px rgba(74,42,114,0.22)" }}>
              <div className="flex items-center gap-4 mb-5">
                <span className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
                      style={{ background: "linear-gradient(135deg, var(--cs-purple), var(--cs-blue-dk))" }}>
                  <Heart className="w-5 h-5 text-white" strokeWidth={2.2} />
                </span>
                <h3 className="font-serif-display text-2xl md:text-3xl" style={{ color: "var(--cs-navy)" }}>
                  How a Session Can Help You
                </h3>
              </div>
              <p className="text-[#2a1f4a] leading-relaxed mb-4">
                Whether you are navigating a major life transition, experiencing physical or emotional pain, feeling stuck, or simply curious about the mind&ndash;body&ndash;spirit connection, Verna&apos;s work can support you in:
              </p>
              <ul className="space-y-2.5 pl-1">
                {benefits.map((b, i) => (
                  <li key={i} className="flex items-start gap-3 text-[#2a1f4a]" data-testid={`healing-benefit-${i}`}>
                    <Sparkles className="w-4 h-4 mt-1 shrink-0" style={{ color: "var(--cs-purple)" }} />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Explore Further */}
            <div className="box-pastel-lavender p-8 md:p-10" data-testid="healing-resources" style={{ boxShadow: "0 12px 40px rgba(74,42,114,0.22)" }}>
              <div className="flex items-center gap-4 mb-5">
                <span className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
                      style={{ background: "linear-gradient(135deg, var(--cs-purple), var(--cs-blue-dk))" }}>
                  <Compass className="w-5 h-5 text-white" strokeWidth={2.2} />
                </span>
                <h3 className="font-serif-display text-2xl md:text-3xl" style={{ color: "var(--cs-navy)" }}>
                  Explore Further
                </h3>
              </div>
              <p className="text-[#2a1f4a] leading-relaxed mb-4">
                To learn more about Verna&apos;s work, you are warmly invited to:
              </p>
              <ul className="space-y-3">
                {externalLinks.map((link, i) => {
                  const Icon = link.icon;
                  return (
                    <li key={i}>
                      <a
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-start gap-3 group p-3 -mx-3 rounded-xl transition hover:bg-white/30"
                        data-testid={`healing-resource-${i}`}
                      >
                        <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 mt-0.5"
                              style={{ background: "linear-gradient(135deg, var(--cs-purple), var(--cs-blue-dk))" }}>
                          <Icon className="w-4 h-4 text-white" />
                        </span>
                        <div>
                          <p className="text-[#131a48] font-medium group-hover:underline">{link.label}</p>
                          <p className="text-xs mt-0.5" style={{ color: "rgba(19,26,72,0.65)" }}>{link.sub}</p>
                        </div>
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Booking CTA */}
            <div className="box-dark p-8 md:p-10" data-testid="healing-cta" style={{ boxShadow: "0 18px 50px rgba(15,20,55,0.45)" }}>
              <p className="text-xs uppercase tracking-[0.3em] mb-3" style={{ color: "var(--cs-cyan)" }}>Energy Healing Session</p>
              <h3 className="font-serif-display text-3xl md:text-4xl text-white mb-2">
                One hour · <span style={{ color: "var(--cs-cyan)" }}>$NZ 150</span>
              </h3>
              <p className="text-slate-200 leading-relaxed mb-6">
                Sessions are deeply restorative — give yourself the gift of one hour with Verna.
              </p>
              <div className="flex flex-wrap gap-4">
                <a href={CONTACT_INFO.phoneHref} className="btn-pill-primary" data-testid="healing-call-btn">
                  <Phone className="w-4 h-4" /> Call Verna · {CONTACT_INFO.phone}
                </a>
                <a href={CONTACT_INFO.emailHref} className="btn-pill-blue" data-testid="healing-email-btn">
                  <Mail className="w-4 h-4" /> {CONTACT_INFO.email}
                </a>
              </div>
            </div>

            {/* Workshops & Retreats */}
            <div className="box-pastel-lavender p-8 md:p-10" data-testid="healing-workshops" style={{ boxShadow: "0 12px 40px rgba(74,42,114,0.22)" }}>
              <div className="flex items-center gap-4 mb-5">
                <span className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
                      style={{ background: "linear-gradient(135deg, var(--cs-purple), var(--cs-blue-dk))" }}>
                  <Leaf className="w-5 h-5 text-white" strokeWidth={2.2} />
                </span>
                <h3 className="font-serif-display text-2xl md:text-3xl" style={{ color: "var(--cs-navy)" }}>
                  Workshops &amp; Retreats
                </h3>
              </div>
              <p className="text-[#2a1f4a] leading-relaxed">
                Verna also runs workshops on Tarot and Energy Healing. For a deeper spiritual experience, contact Verna to enquire about her weekend and week-long retreats through the{" "}
                <a
                  href="https://lightheartedhealingcentre.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium underline-offset-4 hover:underline"
                  style={{ color: "var(--cs-purple)" }}
                  data-testid="lighthearted-link"
                >
                  Light-Hearted Healing Centre and Expansion School
                </a>
                , held in the stunning surroundings of Mercury Bay, Coromandel, New Zealand.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default EnergyHealingSection;
